/**
 * ExtractionStatusBar Component
 * TASK-373: AI-Assisted KYC Filling Workspace
 *
 * Displays extraction progress with color-coded segments showing
 * how many fields are auto-filled, need review, or are missing.
 *
 * TASK-817: this bar could only count the fields that had arrived, so an extraction still
 * running looked exactly like an extraction that found nothing — and the bar told the user
 * to go request the missing evidence from the client, for a document the platform was
 * holding and reading at that moment. It now takes the extraction's own state as an input:
 * documents being read, and documents that could not be read, are shown as themselves.
 */

import React from 'react';
import { CheckCircle2, AlertTriangle, CircleDashed, Loader2, FileWarning, RotateCw } from 'lucide-react';

import type { ClientExtractionStatus } from '../../../services/documentService';

interface ExtractionStatusBarProps {
  autoFilled: number;
  needsReview: number;
  missing: number;
  total: number;
  /** TASK-817: live extraction state for this client. Null while it is unknown. */
  extraction?: ClientExtractionStatus | null;
  /** TASK-817: re-run a failed or abandoned extraction. */
  onRetryExtraction?: (documentId: string) => void;
  /** Document ids with a retry request in flight, so the button can't be double-fired. */
  retryingIds?: string[];
}

const ExtractionStatusBar: React.FC<ExtractionStatusBarProps> = ({
  autoFilled,
  needsReview,
  missing,
  total,
  extraction,
  onRetryExtraction,
  retryingIds = [],
}) => {
  const safeTotal = Math.max(total, 1);
  const autoFilledPct = (autoFilled / safeTotal) * 100;
  const needsReviewPct = (needsReview / safeTotal) * 100;
  const missingPct = (missing / safeTotal) * 100;
  const overallPct = Math.round(((autoFilled + needsReview) / safeTotal) * 100);

  // TASK-817: the documents currently being read, and the ones that could not be.
  const extracting = extraction?.extracting ?? 0;
  const unreadable = extraction?.documents.filter((d) => d.state !== 'extracting') ?? [];
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  // TASK-658: one glanceable answer — what state is this record in, and what is
  // the next step? Confidence/extraction counts stay subordinate to the state.
  //
  // TASK-817 changes exactly one branch of this: "missing fields" is only the whole truth
  // once nothing is still being read. While an extraction is in flight the same emptiness
  // means "not read yet", and saying "Collecting · N still missing" invites the user to go
  // chase evidence that is already here. Review still outranks it — a suggestion waiting on
  // a human is actionable now, and a document being read is not.
  const overallState =
    needsReview > 0
      ? { label: `Needs your review · ${plural(needsReview, 'suggestion')}`, tone: 'text-yellow-800' }
      : extracting > 0
        ? { label: `Reading ${plural(extracting, 'document')} · findings go to your review`, tone: 'text-blue-700' }
        : missing > 0
          ? { label: `Collecting · ${plural(missing, 'field')} still missing`, tone: 'text-gray-700' }
          : { label: 'All tracked fields verified', tone: 'text-green-700' };
  const nextAction =
    needsReview > 0
      ? 'Next: review the extracted suggestions in the assistant panel — accept, edit, or reject each.'
      : extracting > 0
        ? 'Next: nothing yet — wait for the documents to finish, then review what they propose.'
        : unreadable.length > 0 && missing > 0
          ? `Next: retry the ${plural(unreadable.length, 'document')} that could not be read, or request the missing evidence from the client.`
          : missing > 0
            ? 'Next: continue the form or request the missing evidence from the client.'
            : 'Next: submit the record for compliance review.';

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      {/* TASK-817: work in flight, named. Without this band the counts below are the only
          thing on screen, and they cannot tell "not read yet" from "nothing found". */}
      {extracting > 0 && (
        <div
          className="flex items-center gap-2 mb-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-100"
          data-testid="kyc-extraction-inflight"
        >
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin shrink-0" />
          <span className="text-sm text-blue-900">
            Reading {plural(extracting, 'document')} — suggestions appear here as each finishes.
          </span>
        </div>
      )}

      {/* TASK-817: a failed or abandoned read, with the way out. Silence here is what sent
          users to the client to re-request a document the platform already had. */}
      {unreadable.length > 0 && (
        <div
          className="mb-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200"
          data-testid="kyc-extraction-unreadable"
        >
          <div className="flex items-center gap-2 mb-1">
            <FileWarning className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm font-medium text-amber-900">
              {plural(unreadable.length, 'document')} could not be read — no fields were taken from
              {unreadable.length === 1 ? ' it' : ' them'}.
            </span>
          </div>
          <ul className="space-y-1">
            {unreadable.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-3 text-xs">
                <span className="text-amber-900 truncate" title={doc.error || undefined}>
                  {doc.filename}
                  <span className="text-amber-700">
                    {' · '}
                    {doc.state === 'stalled'
                      ? 'stopped partway through'
                      : doc.error || 'extraction failed'}
                  </span>
                </span>
                {onRetryExtraction && (
                  <button
                    type="button"
                    onClick={() => onRetryExtraction(doc.id)}
                    disabled={retryingIds.includes(doc.id)}
                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-amber-300
                               text-amber-900 hover:bg-amber-100 disabled:opacity-50 shrink-0"
                  >
                    <RotateCw
                      className={`w-3 h-3 ${retryingIds.includes(doc.id) ? 'animate-spin' : ''}`}
                    />
                    Retry
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Overall state + explained denominator (TASK-658) */}
      <div className="flex items-center justify-between mb-1.5" data-testid="kyc-status-summary">
        <span className={`text-sm font-semibold ${overallState.tone}`}>{overallState.label}</span>
        <span
          className="text-sm text-gray-600"
          title={`Verified ${autoFilled} + pending ${needsReview} of ${total} tracked KYC fields`}
        >
          {overallPct}% of {total} tracked fields
        </span>
      </div>

      {/* Summary row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-green-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Verified: {autoFilled}/{total}
          </span>
          <span className="flex items-center gap-1.5 text-yellow-700">
            <AlertTriangle className="w-3.5 h-3.5" />
            Pending review: {needsReview}
          </span>
          <span className="flex items-center gap-1.5 text-gray-500">
            <CircleDashed className="w-3.5 h-3.5" />
            Missing: {missing}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden flex">
        {autoFilledPct > 0 && (
          <div
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${autoFilledPct}%` }}
            title={`Verified: ${autoFilled}`}
          />
        )}
        {needsReviewPct > 0 && (
          <div
            className="h-full bg-yellow-400 transition-all duration-500"
            style={{ width: `${needsReviewPct}%` }}
            title={`Pending review: ${needsReview}`}
          />
        )}
        {missingPct > 0 && (
          <div
            className="h-full bg-gray-300 transition-all duration-500"
            style={{ width: `${missingPct}%` }}
            title={`Missing: ${missing}`}
          />
        )}
      </div>

      {/* Persistent next action (TASK-658) */}
      <p className="mt-1.5 text-xs text-gray-600" data-testid="kyc-next-action">
        {nextAction}
      </p>
    </div>
  );
};

export default ExtractionStatusBar;
